import React, { useState, useEffect, Fragment, useRef } from "react";
import {
    AlertTriangle,
    Info,
    Calendar,
    Layout,
    Grid3X3,
    Plus,
    Check,
    Download,
    FileSpreadsheet,
    Save,
    Upload,
    X,
    Navigation,
    Clock,
    Trash2,
    Loader2
} from "lucide-react";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import { toast } from "react-hot-toast";

interface PlanMaestroImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const PlanMaestroImportModal: React.FC<PlanMaestroImportModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const [previewData, setPreviewData] = useState<any>(() => {
        try {
            const stored = sessionStorage.getItem('vilox_import_preview');
            return stored ? JSON.parse(stored) : null;
        } catch { return null; }
    });
    const [importing, setImporting] = useState(false);
    const [activeTab, setActiveTab] = useState<'summary' | 'matrix'>(() => {
        return sessionStorage.getItem('vilox_import_tab') as 'summary' | 'matrix' || 'summary';
    });
    const [progress, setProgress] = useState(0);
    const progressInterval = useRef<any>(null);

    const startProgress = () => {
        setProgress(0);
        if (progressInterval.current) clearInterval(progressInterval.current);
        progressInterval.current = setInterval(() => {
            setProgress(prev => {
                if (prev >= 92) return prev;
                return prev + Math.random() * 8;
            });
        }, 250);
    };

    const finishProgress = () => {
        if (progressInterval.current) clearInterval(progressInterval.current);
        setProgress(100);
    };

    // Guardar cambios en sessionStorage
    useEffect(() => {
        if (previewData) {
            sessionStorage.setItem('vilox_import_preview', JSON.stringify(previewData));
        } else {
            sessionStorage.removeItem('vilox_import_preview');
        }
    }, [previewData]);

    useEffect(() => {
        sessionStorage.setItem('vilox_import_tab', activeTab);
    }, [activeTab]);

    if (!isOpen) return null;

    const processFile = async (selectedFile: File) => {
        setLoading(true);
        startProgress();
        try {
            const result = await maintenanceService.previewImportPlanMaestro(selectedFile);
            if (result.success) {
                setPreviewData(result.data);
                toast.success("Análisis completado");
            }
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error al procesar archivo");
        } finally {
            finishProgress();
            setTimeout(() => setLoading(false), 500);
        }
    };

    const handleCellToggle = (itemIdx: number, routineIdx: number) => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            const newItems = [...prev.items];
            const item = { ...newItems[itemIdx] };

            if (item.rutinas_indices.includes(routineIdx)) {
                item.rutinas_indices = item.rutinas_indices.filter((idx: number) => idx !== routineIdx);
            } else {
                item.rutinas_indices = [...item.rutinas_indices, routineIdx];
            }

            newItems[itemIdx] = item;
            return { ...prev, items: newItems };
        });
    };

    const handleItemChange = (idx: number, field: string, value: any) => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            const newItems = [...prev.items];
            newItems[idx] = { ...newItems[idx], [field]: value };
            return { ...prev, items: newItems };
        });
    };

    const handleRoutineChange = (idx: number, field: string, value: any) => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            const newRutinas = [...prev.rutinas];
            newRutinas[idx] = { ...newRutinas[idx], [field]: value };
            return { ...prev, rutinas: newRutinas };
        });
    };

    const addRoutine = () => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            const newRoutine = {
                nombre: "NUEVA RUTINA",
                tipo: "M1",
                frecuencia_km: 0,
                frecuencia_horas: 0,
                frecuencia_meses: 0,
                items_vinculados: []
            };
            return {
                ...prev,
                rutinas: [...(prev.rutinas || []), newRoutine]
            };
        });
    };

    const removeRoutine = (idx: number) => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            const newRutinas = [...prev.rutinas];
            newRutinas.splice(idx, 1);

            const newItems = prev.items.map((item: any) => ({
                ...item,
                rutinas_indices: item.rutinas_indices
                    .filter((i: number) => i !== idx)
                    .map((i: number) => i > idx ? i - 1 : i)
            }));

            return { ...prev, rutinas: newRutinas, items: newItems };
        });
    };

    const handleMetadataChange = (field: string, value: any) => {
        setPreviewData((prev: any) => (prev ? { ...prev, [field]: value } : prev));
    };

    const addItem = () => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            const newItem = {
                codigo: "NUEVO-" + Math.floor(Math.random() * 1000),
                nombre: "NUEVO REPUESTO / SERVICIO",
                categoria: "REPUESTOS",
                cantidad: 1,
                precio_unitario: 0,
                rutinas_indices: []
            };
            return { ...prev, items: [...prev.items, newItem] };
        });
    };

    const removeItem = (idx: number) => {
        setPreviewData((prev: any) => {
            if (!prev) return prev;
            const newItems = [...prev.items];
            newItems.splice(idx, 1);
            return { ...prev, items: newItems };
        });
    };

    const verifyItemCatalog = async (idx: number, code: string) => {
        if (!code) return;
        try {
            const response = await maintenanceService.getItemsCatalogo({
                codigo_interno: code,
                exact: true
            });

            setPreviewData((prev: any) => {
                if (!prev) return prev;
                const newItems = [...prev.items];
                const item = { ...newItems[idx] };

                if (response.success && response.data.length > 0) {
                    const catalogItem = response.data[0];
                    item.exists_in_catalog = true;
                    item.has_conflict = catalogItem.categoria !== item.categoria || catalogItem.codigo_interno !== item.codigo;
                    item.catalog_category = catalogItem.categoria;
                    item.catalog_code = catalogItem.codigo_interno;
                    item.is_match_by_name = catalogItem.nombre === item.nombre;
                    item.related_plans = catalogItem.planes_asociados || [];

                    if ((!item.precio_unitario || item.precio_unitario === 0) && catalogItem.precio_unitario > 0) {
                        item.precio_unitario = catalogItem.precio_unitario;
                    }
                } else {
                    item.exists_in_catalog = false;
                    item.has_conflict = false;
                    item.catalog_category = undefined;
                    item.catalog_code = undefined;
                    item.related_plans = [];
                }

                newItems[idx] = item;
                return { ...prev, items: newItems };
            });
        } catch (error) {
            console.error("Error verifying item", error);
        }
    };

    const handleImport = async () => {
        if (!previewData) return;

        // OPTIMIZACIÓN: Construimos un payload limpio (Clean Data Pattern)
        // Eliminamos campos exclusivos de la UI y aseguramos tipos correctos
        const cleanPayload = {
            nombre: previewData.nombre,
            descripcion: previewData.descripcion || `Importado: ${previewData.nombre}`,
            modelo_vehiculo: previewData.modelo_vehiculo,
            motor: previewData.motor,
            transmision: previewData.transmision,
            tipo_operacion: previewData.tipo_operacion,

            // 1. Catálogo de items optimizado
            items: previewData.items.map((it: any) => ({
                codigo: it.codigo,
                nombre: it.nombre,
                categoria: it.categoria,
                unidad: it.unidad,
                cantidad: Number(it.cantidad) || 0,
                precio_unitario: Number(it.precio_unitario) || 0,
                tipo: it.categoria === 'MANO DE OBRA' ? 'SERVICIO' : 'CONSUMIBLE'
            })),

            // 2. Rutinas con vinculaciones directas (evitando recalculado en backend)
            rutinas: previewData.rutinas.map((r: any, rIdx: number) => ({
                nombre: r.nombre,
                tipo: r.tipo || 'M1',
                frecuencia_km: Number(r.frecuencia_km) || 0,
                frecuencia_horas: Number(r.frecuencia_horas) || 0,
                frecuencia_meses: Number(r.frecuencia_meses) || 0,
                items_vinculados: previewData.items
                    .filter((it: any) => it.rutinas_indices?.includes(rIdx))
                    .map((it: any) => ({
                        codigo: it.codigo,
                        cantidad: Number(it.cantidad) || 0
                    }))
            }))
        };

        setImporting(true);
        startProgress();
        try {
            const result = await maintenanceService.importPlanMaestro(cleanPayload);
            if (result.success) {
                finishProgress();
                toast.success("Plan Maestro importado con éxito");
                setTimeout(() => {
                    onSuccess();
                    onClose();
                }, 600);
            } else {
                finishProgress();
            }
        } catch (error) {
            finishProgress();
            toast.error("Error al importar el plan");
        } finally {
            setTimeout(() => setImporting(false), 800);
        }
    };

    const handleDiscard = () => {
        setPreviewData(null);
        setActiveTab('summary');
        toast("Carga descartada", { icon: '🗑️' });
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 text-slate-800 antialiased font-sans">
            <div className="bg-white w-full max-w-[95vw] 2xl:max-w-[1440px] rounded-lg shadow-2xl border border-slate-200 flex flex-col h-[90vh] overflow-hidden">

                {/* Header - Fixed Height */}
                <div className="px-8 py-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-black rounded flex items-center justify-center text-white shadow-lg">
                            <Upload size={20} />
                        </div>
                        <div>
                            <h3 className="text-sm font-black tracking-tight text-black uppercase">Importador de Planes</h3>
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.15em] mt-0.5">Control Técnico de Activos • v2.7</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        {previewData && (
                            <div className="flex bg-slate-50 p-1 rounded border border-slate-200">
                                <button
                                    onClick={() => setActiveTab('summary')}
                                    className={`flex items-center gap-2 px-5 py-2 rounded text-[10px] font-black uppercase transition-all ${activeTab === 'summary' ? 'bg-white text-black shadow-sm border border-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    <Layout size={14} /> Resumen
                                </button>
                                <button
                                    onClick={() => setActiveTab('matrix')}
                                    className={`flex items-center gap-2 px-5 py-2 rounded text-[10px] font-black uppercase transition-all ${activeTab === 'matrix' ? 'bg-white text-black shadow-sm border border-slate-200' : 'text-slate-400 hover:text-slate-600'}`}
                                >
                                    <Grid3X3 size={14} /> Matrix de Trabajo
                                </button>
                            </div>
                        )}
                        <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded text-slate-400 transition-colors">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden flex flex-col bg-white min-h-0">
                    {!previewData ? (
                        <div className="flex-1 p-12 flex flex-col items-center justify-center space-y-8 animate-in fade-in zoom-in duration-300">
                            {loading ? (
                                <div className="flex flex-col items-center gap-6 w-full max-w-md animate-in fade-in duration-300">
                                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center relative">
                                        <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
                                        <div className="absolute inset-0 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                    <div className="w-full space-y-3">
                                        <div className="flex justify-between items-end">
                                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Analizando estructura del archivo...</p>
                                            <span className="text-xl font-black text-blue-600">{Math.round(progress)}%</span>
                                        </div>
                                        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                                            <div 
                                                className="h-full bg-blue-600 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(37,99,235,0.4)]" 
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                        <p className="text-[9px] text-slate-400 text-center uppercase font-bold tracking-widest animate-pulse">
                                            Extrayendo rutinas, actividades y repuestos
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div
                                        className="w-full max-w-xl h-64 border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center text-center transition-all bg-slate-50/50 hover:bg-white hover:border-black group relative"
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            if (e.dataTransfer.files[0]) processFile(e.dataTransfer.files[0]);
                                        }}
                                    >
                                        <input type="file" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" onChange={(e) => e.target.files && processFile(e.target.files[0])} accept=".xlsx,.xls,.pdf" />
                                        <Upload size={32} className="text-slate-200 group-hover:text-black mb-4 transition-colors" />
                                        <h4 className="text-xs font-black text-black uppercase tracking-widest">Cargar archivo de mantenimiento</h4>
                                        <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold tracking-widest">Formatos: Excel (.xlsx, .xls) o PDF</p>
                                    </div>

                                    <div className="flex justify-center w-full max-w-xl">
                                        <div className="w-full max-w-xs p-5 rounded border border-slate-100 bg-white shadow-sm flex flex-col group hover:border-black transition-colors">
                                            <div className="flex items-center gap-3 mb-2">
                                                <FileSpreadsheet size={16} className="text-slate-300 group-hover:text-black" />
                                                <span className="text-[10px] font-black uppercase tracking-widest">Excel Template</span>
                                            </div>
                                            <p className="text-[10px] text-slate-500 mb-4 leading-relaxed font-medium">Estructura recomendada para una detección óptima de rutinas y repuestos.</p>
                                            <button onClick={() => maintenanceService.downloadTemplate()} className="mt-auto text-[10px] font-black uppercase text-black hover:underline flex items-center gap-1">
                                                <Download size={12} /> Descargar Plantilla
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 overflow-hidden flex flex-col animate-in slide-in-from-bottom-2 duration-300 min-h-0 relative">
                            {/* Barra de Progreso Superior para Importación Final */}
                            {importing && (
                                <div className="absolute top-0 left-0 right-0 h-1 z-50 bg-slate-100 overflow-hidden">
                                    <div 
                                        className="h-full bg-blue-600 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(37,99,235,0.6)]" 
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            )}
                            {/* Info Banner */}
                            <div className="px-8 py-3 bg-black flex items-center justify-between shrink-0">
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                                        <p className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.1em]">
                                            <span className="text-white">Nota:</span> Los ítems se agrupan automáticamente según los encabezados.
                                        </p>
                                    </div>

                                    {/* Contadores Dinámicos */}
                                    <div className="h-4 w-[1px] bg-slate-700" />
                                    <div className="flex items-center gap-4">
                                        <div className="flex items-center gap-2">
                                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Ítems:</span>
                                            <span className="text-[11px] font-black text-white bg-blue-600 px-2 py-0.5 rounded-md shadow-sm">{previewData.items?.length || 0}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Rutinas:</span>
                                            <span className="text-[11px] font-black text-white bg-emerald-600 px-2 py-0.5 rounded-md shadow-sm">{previewData.rutinas?.length || 0}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest italic">
                                    Motor de Sincronización v2.7 Active
                                </div>
                            </div>

                            {/* Toolbar - Fixed Height */}
                            <div className="px-8 py-4 bg-slate-50 border-b border-slate-200 grid grid-cols-12 gap-3 items-end shrink-0">
                                <div className="col-span-3 flex flex-col gap-1.5">
                                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] ml-0.5">Nombre del Plan Maestro</label>
                                    <input
                                        className="w-full h-8 bg-white border border-slate-200 rounded px-3 text-[10px] font-black uppercase outline-none focus:border-black transition-colors"
                                        value={previewData.nombre}
                                        onChange={(e) => handleMetadataChange('nombre', e.target.value)}
                                    />
                                </div>
                                <div className="col-span-2 flex flex-col gap-1.5">
                                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] ml-0.5">Modelo</label>
                                    <input
                                        className="w-full h-8 bg-white border border-slate-200 rounded px-3 text-[10px] font-black uppercase outline-none focus:border-black transition-colors"
                                        value={previewData.modelo_vehiculo}
                                        onChange={(e) => handleMetadataChange('modelo_vehiculo', e.target.value)}
                                    />
                                </div>
                                <div className="col-span-1 flex flex-col gap-1.5">
                                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] ml-0.5">Motor</label>
                                    <input
                                        className="w-full h-8 bg-white border border-slate-200 rounded px-3 text-[10px] font-black uppercase outline-none focus:border-black transition-colors"
                                        value={previewData.motor}
                                        onChange={(e) => handleMetadataChange('motor', e.target.value)}
                                    />
                                </div>
                                <div className="col-span-1 flex flex-col gap-1.5">
                                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] ml-0.5">Trans.</label>
                                    <input
                                        className="w-full h-8 bg-white border border-slate-200 rounded px-3 text-[10px] font-black uppercase outline-none focus:border-black transition-colors"
                                        value={previewData.transmision}
                                        onChange={(e) => handleMetadataChange('transmision', e.target.value)}
                                    />
                                </div>
                                <div className="col-span-2 flex flex-col gap-1.5">
                                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] ml-0.5">Op.</label>
                                    <select
                                        className="w-full h-8 bg-white border border-slate-200 rounded px-3 text-[10px] font-black uppercase outline-none focus:border-black transition-colors"
                                        value={previewData.tipo_operacion}
                                        onChange={(e) => handleMetadataChange('tipo_operacion', e.target.value)}
                                    >
                                        <option value="CARRETERA">CARRETERA</option>
                                        <option value="FUERA DE CARRETERA">FUERA DE CARRETERA</option>
                                        <option value="MIXTO">MIXTO</option>
                                    </select>
                                </div>
                                <div className="col-span-3 flex gap-2">
                                    <button onClick={addItem} className="flex-1 h-8 flex items-center justify-center gap-2 bg-white border border-slate-300 text-black px-4 rounded text-[9px] font-black uppercase hover:bg-slate-50 transition-all">
                                        <Plus size={14} /> Ítem
                                    </button>
                                    <button onClick={addRoutine} className="flex-1 h-8 flex items-center justify-center gap-2 bg-black text-white px-4 rounded text-[9px] font-black uppercase hover:bg-slate-900 transition-all shadow-md">
                                        <Plus size={14} /> Rutina
                                    </button>
                                </div>
                            </div>

                            {/* Main Tab Area - Must Grow and Contain Scroll */}
                            <div className="flex-1 overflow-hidden p-6 flex flex-col min-h-0">
                                {activeTab === 'summary' ? (
                                    <div className="flex-1 overflow-y-auto pr-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 scrollbar-thin scrollbar-thumb-slate-200">
                                        {previewData.rutinas?.map((r: any, idx: number) => (
                                            <div key={idx} className="p-6 bg-white border border-slate-100 rounded shadow-sm flex flex-col relative group hover:border-black transition-colors h-fit">
                                                <button onClick={() => removeRoutine(idx)} className="absolute top-4 right-4 text-slate-200 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                                                    <Trash2 size={14} />
                                                </button>
                                                <span className="text-[8px] font-black text-slate-300 uppercase tracking-widest mb-1">C0-0{idx + 1}</span>
                                                <h4 className="text-[12px] font-black text-black uppercase border-b border-slate-50 pb-3 mb-4">{r.nombre}</h4>
                                                <div className="space-y-4 flex-1">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2"><Navigation size={12} className="text-slate-200" /><span className="text-[9px] uppercase font-bold text-slate-400">Distancia</span></div>
                                                        <span className="text-[10px] font-black">{r.frecuencia_km?.toLocaleString()} KM</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2"><Clock size={12} className="text-slate-200" /><span className="text-[9px] uppercase font-bold text-slate-400">Horas</span></div>
                                                        <span className="text-[10px] font-black">{r.frecuencia_horas} HRS</span>
                                                    </div>
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2"><Calendar size={12} className="text-slate-200" /><span className="text-[9px] uppercase font-bold text-slate-400">Tiempo</span></div>
                                                        <span className="text-[10px] font-black">{r.frecuencia_meses} Meses</span>
                                                    </div>
                                                </div>
                                                <div className="mt-6 pt-4 border-t border-slate-50 flex items-center justify-between">
                                                    <span className="text-[9px] font-black uppercase text-slate-400 tracking-widest">
                                                        {previewData.items?.filter((it: any) => it.rutinas_indices?.includes(idx)).length || 0} Activaciones
                                                    </span>
                                                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[9px] font-black">{r.tipo || 'M1'}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex-1 border border-slate-200 rounded-lg overflow-hidden flex flex-col min-h-0 bg-white shadow-xl mt-2">
                                        {/* THE SCROLL CONTAINER */}
                                        <div className="flex-1 overflow-auto scrollbar-thin scrollbar-thumb-slate-300 bg-slate-50/30">
                                            <table className="w-full text-left border-collapse table-fixed min-w-max">
                                                <thead className="sticky top-0 z-30 bg-white">
                                                    <tr className="border-b-2 border-slate-100">
                                                        <th className="px-8 py-6 sticky left-0 z-40 bg-white border-r border-slate-100 w-[500px] shadow-[4px_0_10px_rgba(0,0,0,0.03)]">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded bg-slate-900 flex items-center justify-center text-white shadow-md">
                                                                    <Layout size={16} />
                                                                </div>
                                                                <span className="text-[12px] font-black text-black uppercase tracking-[0.1em]">Listado de Actividades / Insumos</span>
                                                            </div>
                                                        </th>
                                                        {previewData.rutinas?.map((r: any, idx: number) => (
                                                            <th key={idx} className="px-4 py-6 border-r border-slate-100 text-center w-[180px] group/col min-w-[180px] bg-slate-50/50">
                                                                <div className="flex flex-col gap-4 relative">
                                                                    <button onClick={() => removeRoutine(idx)} className="absolute -top-3 right-0 text-slate-300 hover:text-red-500 opacity-0 group-hover/col:opacity-100 transition-all">
                                                                        <Trash2 size={12} />
                                                                    </button>
                                                                    <input
                                                                        className="bg-transparent text-center font-black text-[16px] uppercase w-full outline-none focus:text-blue-600 placeholder:text-slate-300"
                                                                        value={r.nombre}
                                                                        onChange={(e) => handleRoutineChange(idx, 'nombre', e.target.value)}
                                                                    />
                                                                    <div className="flex flex-col gap-1.5 items-center">
                                                                        {/* Frecuencias Compactas en Horizontal */}
                                                                        <div className="grid grid-cols-3 gap-1 w-full">
                                                                            <div className="flex flex-col items-center bg-white border border-slate-200 rounded-md py-1.5 hover:border-black transition-all shadow-sm">
                                                                                <Navigation size={10} className="text-slate-300 mb-1" />
                                                                                <input className="bg-transparent w-full text-center outline-none text-[10px] font-black" value={r.frecuencia_km} onChange={(e) => handleRoutineChange(idx, 'frecuencia_km', parseInt(e.target.value) || 0)} />
                                                                                <span className="text-[7px] font-black text-slate-400 uppercase">km</span>
                                                                            </div>
                                                                            <div className="flex flex-col items-center bg-white border border-slate-200 rounded-md py-1.5 hover:border-black transition-all shadow-sm">
                                                                                <Clock size={10} className="text-slate-300 mb-1" />
                                                                                <input className="bg-transparent w-full text-center outline-none text-[10px] font-black" value={r.frecuencia_horas} onChange={(e) => handleRoutineChange(idx, 'frecuencia_horas', parseInt(e.target.value) || 0)} />
                                                                                <span className="text-[7px] font-black text-slate-400 uppercase">hrs</span>
                                                                            </div>
                                                                            <div className="flex flex-col items-center bg-white border border-slate-200 rounded-md py-1.5 hover:border-black transition-all shadow-sm">
                                                                                <Calendar size={10} className="text-slate-300 mb-1" />
                                                                                <input className="bg-transparent w-full text-center outline-none text-[10px] font-black" value={r.frecuencia_meses} onChange={(e) => handleRoutineChange(idx, 'frecuencia_meses', parseInt(e.target.value) || 0)} />
                                                                                <span className="text-[7px] font-black text-slate-400 uppercase">mes</span>
                                                                            </div>
                                                                        </div>

                                                                        <div className="flex items-center w-full bg-slate-900 border border-slate-900 rounded-md px-3 py-1.5 mt-0.5 shadow-lg">
                                                                            <span className="text-[8px] font-black text-slate-500 mr-2 uppercase tracking-tighter italic">TIPO</span>
                                                                            <select
                                                                                className="bg-transparent w-full outline-none text-[10px] font-black text-center text-white appearance-none cursor-pointer"
                                                                                value={r.tipo || 'M1'}
                                                                                onChange={(e) => handleRoutineChange(idx, 'tipo', e.target.value)}
                                                                            >
                                                                                <option value="M1">M1</option>
                                                                                <option value="M2">M2</option>
                                                                                <option value="M3">M3</option>
                                                                                <option value="M4">M4</option>
                                                                                <option value="M5">M5</option>
                                                                                <option value="M6">M6</option>
                                                                            </select>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100 bg-white">
                                                    {previewData.items?.map((item: any, iIdx: number) => (
                                                        <Fragment key={iIdx}>
                                                            {(iIdx === 0 || previewData.items[iIdx - 1].categoria !== item.categoria) && (
                                                                <tr className="bg-slate-900 z-10 sticky left-0">
                                                                    <td
                                                                        colSpan={50}
                                                                        className="px-8 py-3 sticky left-0 bg-slate-900 border-b border-slate-800"
                                                                    >
                                                                        <div className="flex items-center gap-3">
                                                                            <div className="w-1.5 h-4 bg-blue-500 rounded-full" />
                                                                            <span className="text-[11px] font-black text-white uppercase tracking-[0.3em]">
                                                                                {item.categoria}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                            <tr className="group hover:bg-slate-50/50 transition-colors">
                                                                <td className="px-8 py-5 sticky left-0 z-20 bg-white group-hover:bg-slate-50 border-r border-slate-100 shadow-[10px_0_20px_rgba(0,0,0,0.02)]">
                                                                    <div className="flex gap-4 items-center">
                                                                        <div className="flex flex-col flex-1 min-w-0">
                                                                            <div className="flex items-center gap-3">
                                                                                <input
                                                                                    className="bg-transparent font-black text-[15px] uppercase w-full outline-none focus:text-blue-600 truncate text-slate-900"
                                                                                    value={item.nombre}
                                                                                    onChange={(e) => handleItemChange(iIdx, 'nombre', e.target.value)}
                                                                                />
                                                                                <button onClick={() => removeItem(iIdx)} className="text-slate-200 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 shrink-0">
                                                                                    <Trash2 size={16} />
                                                                                </button>
                                                                            </div>
                                                                            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-3 items-center">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-[9px] font-black text-slate-300 uppercase italic">COD:</span>
                                                                                    <input
                                                                                        className="text-[10px] font-black text-slate-900 tracking-wider bg-slate-50 border border-slate-100 rounded px-2 py-0.5 outline-none focus:border-black transition-all w-28 uppercase"
                                                                                        value={item.codigo}
                                                                                        onChange={(e) => handleItemChange(iIdx, 'codigo', e.target.value.toUpperCase())}
                                                                                        onBlur={(e) => verifyItemCatalog(iIdx, e.target.value)}
                                                                                    />
                                                                                </div>

                                                                                <div className="flex items-center gap-2 border-l border-slate-100 pl-4">
                                                                                    <span className="text-[9px] font-black text-slate-300 uppercase italic">UND:</span>
                                                                                    <input
                                                                                        className="text-[10px] font-black text-slate-900 uppercase bg-slate-50 border border-slate-100 rounded px-2 py-0.5 outline-none focus:border-black transition-all w-16"
                                                                                        value={item.unidad}
                                                                                        onChange={(e) => handleItemChange(iIdx, 'unidad', e.target.value.toUpperCase())}
                                                                                    />
                                                                                </div>

                                                                                <div className="flex items-center gap-2 border-l border-slate-100 pl-4">
                                                                                    <span className="text-[9px] font-black text-slate-300 uppercase italic">CANT:</span>
                                                                                    <input
                                                                                        type="number"
                                                                                        className="text-[11px] font-black text-slate-900 bg-slate-50 border border-slate-100 rounded px-2 py-0.5 outline-none focus:border-black transition-all w-20"
                                                                                        value={item.cantidad}
                                                                                        onChange={(e) => handleItemChange(iIdx, 'cantidad', parseFloat(e.target.value) || 0)}
                                                                                    />
                                                                                </div>

                                                                                <div className="flex items-center gap-2 border-l border-slate-100 pl-4 text-emerald-600">
                                                                                    <span className="text-[9px] font-black opacity-40 uppercase italic">PRECIO:</span>
                                                                                    <div className="flex items-center gap-1 bg-slate-50 border border-slate-100 rounded px-2 py-0.5 border-emerald-100">
                                                                                        <span className="text-[10px] font-black">S/</span>
                                                                                        <input
                                                                                            type="number"
                                                                                            step="0.01"
                                                                                            className="text-[11px] font-black text-slate-900 bg-transparent outline-none w-20"
                                                                                            value={item.precio_unitario}
                                                                                            onChange={(e) => handleItemChange(iIdx, 'precio_unitario', parseFloat(e.target.value) || 0)}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <div className="flex flex-col gap-1 mt-3">
                                                                                {/* Alerta de Conflicto de Código/Categoría */}
                                                                                {item.has_conflict && (
                                                                                    <div className="text-[9px] font-black text-amber-600 bg-amber-50 px-3 py-2 rounded-md flex items-center gap-2 border border-amber-200">
                                                                                        <AlertTriangle size={12} strokeWidth={3} className="shrink-0" />
                                                                                        <div className="flex flex-col">
                                                                                            {item.is_match_by_name && (
                                                                                                <span className="tracking-tight uppercase">Código Diferente: El Nombre existe con código <span className="text-amber-800 underline">{item.catalog_code}</span></span>
                                                                                            )}
                                                                                            {item.catalog_category !== item.categoria && (
                                                                                                <span className="tracking-tight uppercase">Categoría distinta: En catálogo es <span className="text-amber-800 underline">{item.catalog_category}</span></span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* Información de Existencia y Planes */}
                                                                                {item.exists_in_catalog && (
                                                                                    <div className="text-[9px] font-black text-blue-600 bg-blue-50 px-3 py-2 rounded-md flex items-center gap-2 border border-blue-200 shadow-sm">
                                                                                        <Info size={12} strokeWidth={3} className="shrink-0" />
                                                                                        <div className="flex flex-col">
                                                                                            <span className="uppercase tracking-tight">Ítem ya registrado en el catálogo maestro</span>
                                                                                            {item.related_plans && item.related_plans.length > 0 ? (
                                                                                                <span className="tracking-tighter uppercase text-[8px] opacity-80 leading-none mt-1">
                                                                                                    Vinculado a: {item.related_plans.join(", ")}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="tracking-tighter uppercase text-[8px] opacity-80 leading-none mt-1 italic">
                                                                                                    Creado anteriormente (sin planes asociados)
                                                                                                </span>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {/* Alerta de Precio Zero */}
                                                                                {(!item.precio_unitario || item.precio_unitario == 0) && (
                                                                                    <div className="text-[9px] font-black text-slate-400 bg-slate-50 px-3 py-2 rounded-md flex items-center gap-2 border border-slate-200">
                                                                                        <AlertTriangle size={12} className="shrink-0" />
                                                                                        <span className="uppercase tracking-tight italic">Advertencia: Este ítem no tiene precio referencial asignado.</span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                {previewData.rutinas?.map((_: any, rIdx: number) => (
                                                                    <td
                                                                        key={rIdx}
                                                                        className="px-4 py-4 text-center cursor-pointer border-r border-slate-50 transition-all hover:bg-slate-100/50"
                                                                        onClick={() => handleCellToggle(iIdx, rIdx)}
                                                                    >
                                                                        <div className="flex justify-center">
                                                                            {item.rutinas_indices.includes(rIdx) ? (
                                                                                <div className="w-10 h-10 bg-black rounded shadow-lg flex items-center justify-center text-white animate-in zoom-in duration-200 border-2 border-black">
                                                                                    <Check size={20} strokeWidth={4} />
                                                                                </div>
                                                                            ) : (
                                                                                <div className="w-10 h-10 rounded-md border-2 border-slate-100 group-hover:border-slate-200 group-hover:bg-white transition-all" />
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        </Fragment>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer - Fixed Height */}
                <div className="px-8 py-5 border-t border-slate-100 bg-white flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-black animate-pulse shadow-[0_0_8px_rgba(0,0,0,0.3)]" />
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Motor de sincronización activo</p>
                    </div>
                    <div className="flex gap-4">
                        <button onClick={handleDiscard} className="px-6 py-2 text-[10px] font-black text-slate-400 hover:text-black uppercase tracking-widest transition-all">Descartar</button>
                        {previewData && (
                            <button
                                onClick={handleImport}
                                disabled={importing}
                                className="bg-black hover:bg-slate-900 text-white px-12 py-3 rounded text-[10px] font-black uppercase tracking-[0.1em] transition-all disabled:opacity-50 flex items-center gap-2 shadow-xl"
                            >
                                {importing ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        PROCESANDO IMPORTACIÓN...
                                    </>
                                ) : (
                                    <>
                                        IMPORTAR PLAN FINAL
                                        <Save size={16} />
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};
